import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';
import { getMeetings, getMeeting, getNotes, getMeetingAnalytics, checkConflicts } from '@/lib/soundstageClient';
import {
    getMeetingsCache, setMeetingsCache,
    getMeetingCache, setMeetingCache,
} from '@/components/soundstage/soundstageIndexedDB';
import type { Meeting, MeetingNote, MeetingAnalytics, ConflictCheckResult } from '@/types/soundstage';

export const soundstageQueryKeys = {
    all: ['soundstage'] as const,
    meetingsAll: (orgId: string, userId: string) =>
        [...soundstageQueryKeys.all, 'meetings', orgId, userId] as const,
    meetings: (orgId: string, userId: string, status?: string, dateFrom?: string, dateTo?: string) =>
        [...soundstageQueryKeys.all, 'meetings', orgId, userId, status, dateFrom, dateTo] as const,
    meeting: (meetingId: string, userId: string) =>
        [...soundstageQueryKeys.all, 'meeting', meetingId, userId] as const,
    notes: (meetingId: string, userId: string) =>
        [...soundstageQueryKeys.all, 'notes', meetingId, userId] as const,
    analytics: (orgId: string, userId: string) =>
        [...soundstageQueryKeys.all, 'analytics', orgId, userId] as const,
    conflicts: (orgId: string, userId: string, participantIds: string[], start: string, end: string) =>
        [...soundstageQueryKeys.all, 'conflicts', orgId, userId, participantIds.join(','), start, end] as const,
};

interface UseMeetingsOptions {
    organizationId: string | undefined;
    userId: string | undefined;
    status?: string;
    dateFrom?: string;
    dateTo?: string;
    enabled?: boolean;
    viewUserProfileId?: string;
}

export function useMeetings({ organizationId, userId, status, dateFrom, dateTo, enabled = true, viewUserProfileId }: UseMeetingsOptions) {
    const queryClient = useQueryClient();
    const queryKey = [...soundstageQueryKeys.meetings(organizationId || '', userId || '', status, dateFrom, dateTo), viewUserProfileId || ''] as const;
    const cacheKey = queryKey.join(':');

    // Seed react-query cache from IndexedDB on mount (instant display)
    useEffect(() => {
        if (!organizationId || !userId) return;
        getMeetingsCache(cacheKey).then(cached => {
            if (cached?.meetings?.length) {
                // Only seed if react-query doesn't already have data
                const existing = queryClient.getQueryData<Meeting[]>(queryKey);
                if (!existing || existing.length === 0) {
                    queryClient.setQueryData(queryKey, cached.meetings);
                }
            }
        });
    }, [cacheKey]);

    const query = useQuery<Meeting[]>({
        queryKey,
        queryFn: async () => {
            if (!organizationId || !userId) return [];
            const data = await getMeetings(organizationId, userId, status, undefined, undefined, dateFrom, dateTo, viewUserProfileId);
            setMeetingsCache(cacheKey, { meetings: data, hasMore: false });
            return data;
        },
        enabled: enabled && !!organizationId && !!userId,
        staleTime: 15_000,
        refetchInterval: 15_000,
        refetchOnWindowFocus: true,
    });

    return query;
}

interface UseMeetingOptions {
    meetingId: string | undefined;
    userId: string | undefined;
    enabled?: boolean;
}

export function useMeeting({ meetingId, userId, enabled = true }: UseMeetingOptions) {
    const queryClient = useQueryClient();
    const queryKey = soundstageQueryKeys.meeting(meetingId || '', userId || '');

    // Seed from IndexedDB
    useEffect(() => {
        if (!meetingId || !userId) return;
        getMeetingCache(meetingId).then(cached => {
            if (cached) {
                const existing = queryClient.getQueryData<Meeting>(queryKey);
                if (!existing) {
                    queryClient.setQueryData(queryKey, cached);
                }
            }
        });
    }, [meetingId]);

    return useQuery<Meeting>({
        queryKey,
        queryFn: async () => {
            if (!meetingId || !userId) throw new Error('Missing params');
            const data = await getMeeting(meetingId, userId);
            // Write to IndexedDB
            setMeetingCache(meetingId, data);
            return data;
        },
        enabled: enabled && !!meetingId && !!userId,
        staleTime: 60 * 1000,
    });
}

interface UseMeetingNotesOptions {
    meetingId: string | undefined;
    userId: string | undefined;
    enabled?: boolean;
}

export function useMeetingNotes({ meetingId, userId, enabled = true }: UseMeetingNotesOptions) {
    return useQuery<MeetingNote[]>({
        queryKey: soundstageQueryKeys.notes(meetingId || '', userId || ''),
        queryFn: async () => {
            if (!meetingId || !userId) return [];
            return getNotes(meetingId, userId);
        },
        enabled: enabled && !!meetingId && !!userId,
        staleTime: 30 * 1000, // 30s — notes don't change frequently
    });
}

interface UseMeetingAnalyticsOptions {
    organizationId: string | undefined;
    userId: string | undefined;
    enabled?: boolean;
}

export function useMeetingAnalytics({ organizationId, userId, enabled = true }: UseMeetingAnalyticsOptions) {
    return useQuery<MeetingAnalytics>({
        queryKey: soundstageQueryKeys.analytics(organizationId || '', userId || ''),
        queryFn: async () => {
            if (!organizationId || !userId) throw new Error('Missing params');
            return getMeetingAnalytics(organizationId, userId);
        },
        enabled: enabled && !!organizationId && !!userId,
        staleTime: 60 * 1000,
    });
}

interface UseConflictCheckOptions {
    organizationId: string | undefined;
    userId: string | undefined;
    participantIds: string[];
    scheduledStartAt: string | undefined;
    scheduledEndAt: string | undefined;
    enabled?: boolean;
}

export function useConflictCheck({
    organizationId,
    userId,
    participantIds,
    scheduledStartAt,
    scheduledEndAt,
    enabled = true,
}: UseConflictCheckOptions) {
    const sortedIds = [...participantIds].sort().join(',');
    const prevKeyRef = useRef('');

    const isReady = enabled && !!organizationId && !!userId && participantIds.length > 0 && !!scheduledStartAt && !!scheduledEndAt;
    const currentKey = isReady ? `${sortedIds}|${scheduledStartAt}|${scheduledEndAt}` : '';

    // Only trigger a new fetch when the key actually changes
    const shouldFetch = isReady && currentKey !== prevKeyRef.current;
    if (shouldFetch) prevKeyRef.current = currentKey;

    return useQuery<ConflictCheckResult>({
        queryKey: soundstageQueryKeys.conflicts(
            organizationId || '',
            userId || '',
            participantIds,
            scheduledStartAt || '',
            scheduledEndAt || '',
        ),
        queryFn: async () => {
            if (!organizationId || !userId || !scheduledStartAt || !scheduledEndAt) {
                return { has_conflicts: false, participant_conflicts: [], alternative_slots: [] };
            }
            return checkConflicts(organizationId, userId, participantIds, scheduledStartAt, scheduledEndAt);
        },
        enabled: isReady,
        staleTime: 30_000,
        refetchOnWindowFocus: false,
    });
}
